/**
 * Comunicaciones de ofertas · leads (12/10/2026), en Marketing.
 *
 * Quien dijo «sí» en un formulario de Meta a recibir ofertas de una marca por
 * email o WhatsApp. Es una lista APARTE de la de pacientes propios (la otra
 * pestaña de Marketing): entra sola (la decide el servidor,
 * services/ofertas_leads.py), con la pregunta tal y como la hizo el formulario,
 * la hora en que la persona lo envió, la marca, el formulario y el anuncio.
 *
 * Aquí se mira, se busca, se exporta en CSV (de alta, o todo con las bajas) y
 * se da de baja a alguien (con confirmación; queda la fecha). Y se le manda una
 * campaña por correo desde el buzón de SU marca, con el mismo envío de
 * Marketing (uno a uno, con el enlace de baja de ESTA lista). WhatsApp no está
 * conectado: para eso, la exportación.
 *
 * Solo administración (como Marketing). Nada de esto sale por el conector.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { EmptyState, MiniStat } from './Common';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  Download, Loader2, Search, Send, UserCheck, UserMinus, Users, AlertTriangle, Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto, telefonoConPrefijo, telefonoParaEnlace } from '../lib/utils';
import { useMarcas } from '../lib/marcas';
import MarcaLead from './MarcaLead';

// La hora de la clínica, la vea quien la vea: la de Madrid.
const EN_MADRID = new Intl.DateTimeFormat('es-ES', {
  timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

/** «10/01/2099, 11:00» en hora de Madrid; "" si no hay fecha. */
export function fechaHora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : EN_MADRID.format(d);
}

const ESTADOS = [
  { clave: 'alta', nombre: 'De alta' },
  { clave: 'baja', nombre: 'De baja' },
  { clave: 'todos', nombre: 'Todos' },
];

function mensajeError(e, porDefecto) {
  const detalle = e?.response?.data?.detail;
  return typeof detalle === 'string' && detalle ? detalle : porDefecto;
}

/** Cómo se dice de alguien en una frase: su nombre, su correo o su teléfono. */
function quienEs(it) {
  return it?.nombre || it?.email || telefonoConPrefijo(it?.telefono || '') || 'esta persona';
}

async function descargar(estado) {
  const r = await api.get('/api/marketing/ofertas-leads/exportar',
    { params: { estado }, responseType: 'blob' });
  const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8;' });
  const hoy = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ofertas_leads_${estado === 'alta' ? 'de_alta' : 'todas'}_${hoy}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function OfertasLeadsPanel({ cuentas = [] }) {
  const marcas = useMarcas();
  // Su historial de campañas, aparte del de pacientes (GET …/ofertas-leads/campanas).
  const [campanas, setCampanas] = useState([]);
  const [items, setItems] = useState([]);
  const [cifras, setCifras] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState('');
  const [estado, setEstado] = useState('alta');
  const [busca, setBusca] = useState('');
  const [exportando, setExportando] = useState('');
  // La baja, con confirmación (y un motivo si quien la da lo sabe).
  const [bajaDe, setBajaDe] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [dandoDeBaja, setDandoDeBaja] = useState(false);
  // La campaña por correo.
  const [elegidos, setElegidos] = useState([]);
  const [campanaOpen, setCampanaOpen] = useState(false);
  const [cuenta, setCuenta] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get('/api/marketing/ofertas-leads');
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setCifras(r.data?.cifras || null);
      setFallo('');
    } catch (e) {
      // Una lista vacía porque el servidor no contestó se leería como «nadie».
      setFallo(mensajeError(e, 'No se ha podido cargar la lista de ofertas'));
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarCampanas = useCallback(async () => {
    try {
      const r = await api.get('/api/marketing/ofertas-leads/campanas');
      setCampanas(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { /* el historial no es imprescindible: la lista sí se enseña */ }
  }, []);

  useEffect(() => { cargar(); cargarCampanas(); }, [cargar, cargarCampanas]);

  // Mientras una campaña esté saliendo, el avance se refresca cada 5 s.
  const enviandoAhora = campanas.some((c) => c.estado === 'enviando');
  useEffect(() => {
    if (!enviandoAhora) return undefined;
    const t = setInterval(cargarCampanas, 5000);
    return () => clearInterval(t);
  }, [enviandoAhora, cargarCampanas]);

  const visibles = useMemo(() => {
    const q = normalizaTexto(busca.trim());
    return items.filter((it) => (estado === 'todos' || it.estado === estado)
      && (!q || String(it.buscar || '').includes(q)));
  }, [items, estado, busca]);

  // Solo se le puede escribir a quien está de alta y tiene correo.
  const conCorreo = useMemo(() => visibles.filter((it) => it.estado === 'alta' && it.email), [visibles]);
  const marcadosVisibles = conCorreo.filter((it) => elegidos.includes(it.id)).length;
  const todosMarcados = conCorreo.length > 0 && marcadosVisibles === conCorreo.length;
  const marcarTodos = () => {
    const ids = conCorreo.map((it) => it.id);
    setElegidos((antes) => (todosMarcados
      ? antes.filter((x) => !ids.includes(x)) : [...new Set([...antes, ...ids])]));
  };
  const alternar = (id) => setElegidos((antes) => (antes.includes(id)
    ? antes.filter((x) => x !== id) : [...antes, id]));

  // El buzón de cada marca (Configuración → Marcas): la campaña sale del buzón
  // de la marca a la que se dio el permiso, y solo a los de esa marca.
  const marcaDelBuzon = (clave) => marcas.find((m) => !m.externa && m.buzon === clave) || null;
  const elegidasDe = (clave) => {
    const m = marcaDelBuzon(clave);
    return m ? items.filter((it) => elegidos.includes(it.id) && it.estado === 'alta'
      && it.email && it.marca === m.clave) : [];
  };
  const paraLaCampana = elegidasDe(cuenta);
  const elegidasDeAlta = items.filter((it) => elegidos.includes(it.id) && it.estado === 'alta' && it.email);

  const exportar = async (cual) => {
    if (exportando) return;
    setExportando(cual);
    try {
      await descargar(cual);
      toast.success(cual === 'alta' ? 'Exportada la lista de alta' : 'Exportada la lista entera, con las bajas');
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido exportar la lista'));
    } finally {
      setExportando('');
    }
  };

  const pedirBaja = (it) => {
    setMotivo('');
    setBajaDe(it);
  };

  const confirmarBaja = async () => {
    if (!bajaDe || dandoDeBaja) return;
    setDandoDeBaja(true);
    try {
      const r = await api.post(`/api/marketing/ofertas-leads/${bajaDe.id}/baja`,
        { motivo: motivo.trim() });
      if (r.data?.ya_estaba) toast.info(r.data.aviso || 'Ya estaba de baja: no se ha cambiado nada');
      else toast.success(`${quienEs(bajaDe)} queda de baja desde hoy: ya no recibirá ofertas`);
      setElegidos((antes) => antes.filter((x) => x !== bajaDe.id));
      setBajaDe(null);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido dar de baja'));
    } finally {
      setDandoDeBaja(false);
    }
  };

  const abrirCampana = () => {
    if (!elegidasDeAlta.length) {
      toast.info('Marca primero a quién se lo mandas');
      return;
    }
    const conElegidas = cuentas.find((c) => c.configurada && elegidasDe(c.clave).length);
    setCuenta((antes) => antes || conElegidas?.clave || '');
    setConfirmando(false);
    setCampanaOpen(true);
  };

  const enviarPrueba = async () => {
    if (!cuenta) return toast.info('Elige el buzón desde el que se manda');
    if (!asunto.trim() || !cuerpo.trim()) return toast.info('Escribe el asunto y el mensaje');
    const destino = window.prompt('¿A qué dirección mando la prueba?', '');
    if (!destino) return undefined;
    setEnviando(true);
    try {
      await api.post('/api/marketing/ofertas-leads/campanas', {
        cuenta, asunto, cuerpo, destinatarios: [], prueba_email: destino,
      });
      toast.success(`Prueba enviada a ${destino}`);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo enviar la prueba'));
    } finally {
      setEnviando(false);
    }
    return undefined;
  };

  const enviarCampana = async () => {
    if (enviando) return;
    setEnviando(true);
    try {
      const r = await api.post('/api/marketing/ofertas-leads/campanas', {
        cuenta, asunto, cuerpo, destinatarios: paraLaCampana.map((it) => it.id),
      });
      const fuera = r.data?.descartados;
      toast.success(`Campaña en marcha: ${r.data?.campana?.total ?? paraLaCampana.length} correos`
        + (fuera ? ` (${fuera} descartados: de baja, sin correo o de otra marca)` : ''));
      setCampanaOpen(false);
      setConfirmando(false);
      setAsunto('');
      setCuerpo('');
      setElegidos([]);
      await Promise.all([cargar(), cargarCampanas()]);
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo lanzar la campaña'));
    } finally {
      setEnviando(false);
    }
  };

  const deEstaLista = campanas;
  const marcaElegida = marcaDelBuzon(cuenta);

  return (
    <div className="space-y-5" data-testid="ofertas-leads">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#13231E]">Comunicaciones de ofertas · leads</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Contactos de los formularios de Meta que respondieron «Sí» a recibir ofertas de una marca
            por email o WhatsApp. Entran solos; es una lista aparte de la de pacientes propios.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={abrirCampana} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="ofertas-leads-nueva-campana">
            <Send className="w-4 h-4 mr-1" /> Enviar campaña por correo{elegidasDeAlta.length ? ` (${elegidasDeAlta.length})` : ''}
          </Button>
          <Button variant="outline" onClick={() => exportar('alta')} disabled={!!exportando}
            data-testid="ofertas-leads-exportar-alta">
            {exportando === 'alta' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Exportar CSV (de alta)
          </Button>
          <Button variant="outline" onClick={() => exportar('todos')} disabled={!!exportando}
            data-testid="ofertas-leads-exportar-todos">
            {exportando === 'todos' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Exportar CSV (todo, con bajas)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MiniStat label="De alta" value={cifras?.alta ?? '—'} icon={UserCheck}
          hint={cifras ? `${cifras.con_email} con correo` : undefined} />
        <MiniStat label="De baja" value={cifras?.baja ?? '—'} icon={UserMinus} />
        <MiniStat label="Total en la lista" value={cifras?.total ?? '—'} icon={Users} />
      </div>

      {cifras?.sin_marca ? (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
          data-testid="ofertas-leads-sin-marca">
          {cifras.sin_marca === 1 ? 'Hay 1 persona' : `Hay ${cifras.sin_marca} personas`} sin marca: su formulario
          (ni su página) no está en Configuración → Marcas, así que no se sabe de qué marca son las ofertas que
          aceptó y no recibe campañas de ninguna. Al registrar el formulario, la marca se pone sola.
        </p>
      ) : null}

      <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="ofertas-leads-whatsapp">
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
        <span>
          WhatsApp no está conectado: para una campaña por WhatsApp, exporta la lista de alta. Quien
          está de baja no sale en esa exportación ni recibe ninguna campaña.
        </span>
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por estado"
          data-testid="ofertas-leads-estados">
          {ESTADOS.map(({ clave, nombre }) => (
            <button key={clave} type="button" aria-pressed={estado === clave}
              data-testid={`ofertas-leads-estado-${clave}`} onClick={() => setEstado(clave)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                estado === clave ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                  : 'bg-background text-muted-foreground hover:text-foreground'}`}>
              {nombre}
              <span className="tabular-nums font-semibold">
                {clave === 'todos' ? (cifras?.total ?? items.length) : (cifras?.[clave] ?? 0)}
              </span>
            </button>
          ))}
        </div>
        <div className="relative max-w-md flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, correo, teléfono, marca o anuncio…" value={busca}
            onChange={(e) => setBusca(e.target.value)} className="pl-10" data-testid="ofertas-leads-buscar" />
        </div>
      </div>

      {fallo ? (
        <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3" data-testid="ofertas-leads-fallo">
          {fallo}. Vuelve a intentarlo: la lista no está vacía, es que no se ha podido leer.
        </div>
      ) : cargando ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : visibles.length === 0 ? (
        <EmptyState icon={Megaphone}
          title={items.length ? 'Nadie en este filtro' : 'Todavía nadie en la lista'}
          description="Entran solos los contactos de los formularios de Meta que responden «Sí» a la pregunta de ofertas." />
      ) : (
        <div className="cjo-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="ofertas-leads-tabla">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/50">
              <tr>
                <th className="py-2.5 px-3 w-10">
                  <input type="checkbox" checked={todosMarcados} onChange={marcarTodos}
                    disabled={!conCorreo.length} aria-label="Marcar a todos los de alta con correo que se ven"
                    data-testid="ofertas-leads-check-todos" />
                </th>
                <th className="py-2.5 px-3">Contacto</th>
                <th className="py-2.5 px-3">Marca</th>
                <th className="py-2.5 px-3">De dónde</th>
                <th className="py-2.5 px-3">Consentimiento</th>
                <th className="py-2.5 px-3">Estado</th>
                <th className="py-2.5 px-3 text-right"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((it) => (
                <tr key={it.id} className="border-t border-border align-top" data-testid={`ofertas-leads-fila-${it.id}`}>
                  <td className="py-2.5 px-3">
                    <input type="checkbox" checked={elegidos.includes(it.id)} onChange={() => alternar(it.id)}
                      disabled={it.estado !== 'alta' || !it.email}
                      title={it.estado !== 'alta' ? 'De baja: no recibe nada' : (it.email ? '' : 'Sin correo: solo por la exportación')}
                      aria-label={`Incluir a ${quienEs(it)} en la campaña`}
                      data-testid={`ofertas-leads-check-${it.id}`} />
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="font-medium text-[#0B1220]">{it.nombre}</div>
                    {it.email ? (
                      <a href={`mailto:${it.email}`} className="block text-xs text-[#13231E] underline">{it.email}</a>
                    ) : null}
                    {it.telefono ? (
                      <a href={`tel:${telefonoParaEnlace(it.telefono)}`} className="block text-xs text-[#13231E] underline whitespace-nowrap">
                        {telefonoConPrefijo(it.telefono)}
                      </a>
                    ) : null}
                  </td>
                  <td className="py-2.5 px-3">
                    <MarcaLead marca={it.marca} marcas={marcas} />
                    {!it.marca ? (
                      <div className="text-[11px] text-amber-800 mt-0.5 max-w-[12rem]" data-testid={`ofertas-leads-sin-marca-${it.id}`}>
                        No recibe campañas hasta que su formulario esté en Configuración → Marcas
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2.5 px-3 text-xs text-muted-foreground">
                    {it.ad_name ? <div>Anuncio «{it.ad_name}»</div> : null}
                    {it.campaign_name ? <div>Campaña «{it.campaign_name}»</div> : null}
                    {it.form_id ? <div>Formulario {it.form_id}</div> : null}
                  </td>
                  <td className="py-2.5 px-3 text-xs">
                    <div data-testid={`ofertas-leads-alta-${it.id}`}>{fechaHora(it.alta_at)}</div>
                    {it.respuesta ? <div className="text-muted-foreground">«{it.respuesta}»</div> : null}
                    {it.pregunta ? (
                      <div className="text-muted-foreground italic" data-testid={`ofertas-leads-pregunta-${it.id}`}>
                        {it.pregunta}
                      </div>
                    ) : null}
                    {it.veces_si > 1 ? <div className="text-muted-foreground">Ha dicho que sí {it.veces_si} veces</div> : null}
                  </td>
                  <td className="py-2.5 px-3 text-xs" data-testid={`ofertas-leads-estado-de-${it.id}`}>
                    {it.estado === 'alta' ? (
                      <span className="inline-block whitespace-nowrap rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-emerald-900">De alta</span>
                    ) : (
                      <>
                        <span className="inline-block whitespace-nowrap rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-slate-800">
                          De baja · {fechaHora(it.baja_at)}
                        </span>
                        <div className="text-muted-foreground mt-0.5">
                          {it.baja_origen_texto}{it.baja_por_nombre ? ` (${it.baja_por_nombre})` : ''}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {it.estado === 'alta' ? (
                      <Button size="sm" variant="outline" onClick={() => pedirBaja(it)}
                        data-testid={`ofertas-leads-baja-${it.id}`}>
                        <UserMinus className="w-4 h-4 mr-1" /> Dar de baja
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deEstaLista.length > 0 ? (
        <div className="cjo-card p-4 space-y-2" data-testid="ofertas-leads-campanas">
          <div className="text-sm font-semibold text-[#13231E]">Campañas enviadas a esta lista</div>
          {deEstaLista.map((c) => (
            <div key={c.id}
              className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-0.5 min-w-0 text-xs border-t border-border pt-2">
              <span className="text-muted-foreground">{c.cuenta_label}</span>
              <span className="font-medium text-[#0B1220] min-w-0 break-words">{c.asunto}</span>
              <span className="text-muted-foreground sm:ml-auto">{fechaHora(c.creada_at)} · {c.creada_por}</span>
              <span>
                {c.estado === 'enviando'
                  ? `${c.enviados}/${c.total} enviando…`
                  : `${c.enviados}/${c.total} enviados${c.fallidos ? ` · ${c.fallidos} fallidos` : ''}${c.omitidos ? ` · ${c.omitidos} se dieron de baja antes` : ''}`}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Dar de baja, con confirmación. */}
      <Dialog open={!!bajaDe} onOpenChange={(abierto) => { if (!abierto) setBajaDe(null); }}>
        <DialogContent className="max-w-md" data-testid="ofertas-leads-baja-dialogo">
          <DialogHeader><DialogTitle>¿Dar de baja a {quienEs(bajaDe)}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Deja de recibir ofertas por email y por WhatsApp desde hoy: no sale en la exportación de
            alta ni recibe ninguna campaña. Se guarda la fecha de la baja y no se borra nada. Solo vuelve
            a estar de alta si responde «Sí» en otro formulario.
          </p>
          <div>
            <label htmlFor="ofertas-leads-baja-motivo" className="text-xs text-muted-foreground">
              Motivo (opcional)
            </label>
            <Input id="ofertas-leads-baja-motivo" value={motivo} maxLength={200}
              onChange={(e) => setMotivo(e.target.value)} placeholder="Pidió por teléfono que no le escribamos…"
              data-testid="ofertas-leads-baja-motivo" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBajaDe(null)} data-testid="ofertas-leads-baja-cancelar">Cancelar</Button>
            <Button onClick={confirmarBaja} disabled={dandoDeBaja} className="bg-red-700 hover:bg-red-800"
              data-testid="ofertas-leads-baja-confirmar">
              {dandoDeBaja ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserMinus className="w-4 h-4 mr-1" />}
              Sí, dar de baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* La campaña por correo: desde el buzón de una marca, a los de esa marca. */}
      <Dialog open={campanaOpen} onOpenChange={(v) => { setCampanaOpen(v); if (!v) setConfirmando(false); }}>
        <DialogContent className="max-w-2xl" data-testid="ofertas-leads-campana-dialogo">
          <DialogHeader>
            <DialogTitle>Campaña por correo a la lista de ofertas</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Se envía desde (el buzón de la marca)
              </div>
              <div className="flex flex-wrap gap-2">
                {cuentas.map((c) => {
                  const m = marcaDelBuzon(c.clave);
                  const n = elegidasDe(c.clave).length;
                  const vale = c.configurada && m;
                  return (
                    <button key={c.clave} type="button" disabled={!vale}
                      onClick={() => { setCuenta(c.clave); setConfirmando(false); }}
                      className={`px-3 py-2 rounded-md border text-left text-xs transition ${
                        cuenta === c.clave ? 'border-[#2E6E5E] bg-[rgba(46,110,94,0.08)]' : 'border-border hover:bg-muted/50'
                      } ${vale ? '' : 'opacity-50 cursor-not-allowed'}`}
                      data-testid={`ofertas-leads-cuenta-${c.clave}`}>
                      <div className="font-semibold text-[#13231E]">{c.label}</div>
                      <div className="text-muted-foreground">{c.email}</div>
                      {!c.configurada ? <div className="text-red-600">Sin conectar</div> : null}
                      {c.configurada && !m ? <div className="text-red-600">No es el buzón de ninguna marca</div> : null}
                      {vale ? <div className="text-muted-foreground">{m.nombre} · {n} elegido{n === 1 ? '' : 's'}</div> : null}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="ofertas-leads-campana-a-quien">
              {marcaElegida
                ? `Va a ${paraLaCampana.length} persona${paraLaCampana.length === 1 ? '' : 's'} que dieron permiso a ${marcaElegida.nombre}.`
                  + (elegidasDeAlta.length > paraLaCampana.length
                    ? ` Las otras ${elegidasDeAlta.length - paraLaCampana.length} elegidas son de otra marca y no lo reciben desde este buzón.`
                    : '')
                : 'Elige el buzón de la marca: cada persona dio permiso para las ofertas de una marca.'}
            </p>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Asunto</div>
              <Input value={asunto} onChange={(e) => setAsunto(e.target.value)} data-testid="ofertas-leads-campana-asunto" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Mensaje</div>
              <Textarea value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} rows={8}
                placeholder={'Hola {nombre},\n\n…'} data-testid="ofertas-leads-campana-cuerpo" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Escribe <code>{'{nombre}'}</code> donde quieras que salga el nombre de cada persona. La firma del
                buzón y el enlace de baja (que la da de baja en esta lista) se añaden solos. Cada uno recibe su
                propio correo, uno por segundo; quien se dé de baja mientras salen ya no lo recibe.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={enviarPrueba} disabled={enviando || !marcaElegida}
              data-testid="ofertas-leads-campana-prueba">
              Mandarme una prueba
            </Button>
            {confirmando ? (
              <Button onClick={enviarCampana} disabled={enviando || !paraLaCampana.length}
                className="bg-red-700 hover:bg-red-800" data-testid="ofertas-leads-campana-confirmar">
                {enviando ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Lanzando…</>
                  : `Sí, enviar a ${paraLaCampana.length}`}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (!marcaElegida) return toast.info('Elige el buzón de la marca');
                  if (!paraLaCampana.length) return toast.info('Ninguna de las elegidas es de esta marca');
                  if (!asunto.trim() || !cuerpo.trim()) return toast.info('Falta el asunto o el mensaje');
                  setConfirmando(true);
                  return undefined;
                }}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="ofertas-leads-campana-enviar">
                <Send className="w-4 h-4 mr-1" /> Enviar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
